import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  Room,
  GameState,
  RoomMessage,
} from '@deadpan/shared';
import { PlayerRole, isNashSchrodinger, isNashStandApart, isNashWheel } from '@deadpan/shared';
import {
  parseInviteRoomCodeFromLocation,
  replaceInviteUrlWithHomeIfMatchingRoom,
} from '../utils/roomInvitePath';

interface SocketContextType {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  isConnected: boolean;
  isMaintenance: boolean;
  currentRoom: Room | null;
  gameState: GameState | null;
  error: string | null;
  myRole: PlayerRole | null;
  myAnswer: string | null;
  mySimilarMindsAnswerId: string | null;
  myGameTheoryWheelForce: number | null;
  myStandApartCardIndex: number | null;
  mySchrodingerObservation: { remainingSeconds: number; holdId: number } | null;
  myAceAttorneyJudgment: string | null;
  myAceAttorneyAnswer: string | null;
  myPlayerId: string | null;
  adoptRoom: (room: Room, playerName: string) => void;
  roomMessages: RoomMessage[];
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  isMaintenance: false,
  currentRoom: null,
  gameState: null,
  error: null,
  myRole: null,
  myAnswer: null,
  mySimilarMindsAnswerId: null,
  myGameTheoryWheelForce: null,
  myStandApartCardIndex: null,
  mySchrodingerObservation: null,
  myAceAttorneyJudgment: null,
  myAceAttorneyAnswer: null,
  myPlayerId: null,
  adoptRoom: () => {},
  roomMessages: [],
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

// SessionStorage keys (tab-specific, unlike localStorage which is shared across tabs)
export const STORAGE_KEYS = {
  ROOM_CODE: 'deadpan:roomCode',
  PLAYER_NAME: 'deadpan:playerName',
  PLAYER_ID: 'deadpan:playerId',
};

const storage = {
  getItem: (key: string) => sessionStorage.getItem(key),
  setItem: (key: string, value: string) => { try { sessionStorage.setItem(key, value); } catch {} },
  removeItem: (key: string) => { try { sessionStorage.removeItem(key); } catch {} },
};

function findSeatPlayer(room: Room, playerId: string | null, playerName: string | null) {
  if (playerId) {
    const byId = room.players.find((player) => player.id === playerId);
    if (byId) return byId;
  }
  if (playerName) {
    const normalized = playerName.toLowerCase().trim();
    return room.players.find((player) => player.name.toLowerCase() === normalized);
  }
  return undefined;
}

export function persistPlayerSeat(room: Room, playerName: string): string | undefined {
  storage.setItem(STORAGE_KEYS.ROOM_CODE, room.code);
  const player = findSeatPlayer(room, storage.getItem(STORAGE_KEYS.PLAYER_ID), playerName);
  if (player) {
    storage.setItem(STORAGE_KEYS.PLAYER_NAME, player.name);
    storage.setItem(STORAGE_KEYS.PLAYER_ID, player.id);
    return player.id;
  }
  storage.setItem(STORAGE_KEYS.PLAYER_NAME, playerName.trim());
  return undefined;
}

export function clearPlayerSeat(): void {
  storage.removeItem(STORAGE_KEYS.ROOM_CODE);
  storage.removeItem(STORAGE_KEYS.PLAYER_NAME);
  storage.removeItem(STORAGE_KEYS.PLAYER_ID);
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [myRole, setMyRole] = useState<PlayerRole | null>(null);
  const [myAnswer, setMyAnswer] = useState<string | null>(null);
  const [mySimilarMindsAnswerId, setMySimilarMindsAnswerId] = useState<string | null>(null);
  const [myGameTheoryWheelForce, setMyGameTheoryWheelForce] = useState<number | null>(null);
  const [myStandApartCardIndex, setMyStandApartCardIndex] = useState<number | null>(null);
  const [myAceAttorneyJudgment, setMyAceAttorneyJudgment] = useState<string | null>(null);
  const [myAceAttorneyAnswer, setMyAceAttorneyAnswer] = useState<string | null>(null);
  const [mySchrodingerObservation, setMySchrodingerObservation] = useState<{
    remainingSeconds: number;
    holdId: number;
  } | null>(null);
  const [roomMessages, setRoomMessages] = useState<RoomMessage[]>([]);
  const [myPlayerId, setMyPlayerId] = useState<string | null>(() => storage.getItem(STORAGE_KEYS.PLAYER_ID));
  const myPlayerIdRef = useRef<string | null>(myPlayerId);
  myPlayerIdRef.current = myPlayerId;

  const rememberSeat = (room: Room, playerName?: string | null) => {
    const player = findSeatPlayer(
      room,
      myPlayerIdRef.current || storage.getItem(STORAGE_KEYS.PLAYER_ID),
      playerName ?? storage.getItem(STORAGE_KEYS.PLAYER_NAME),
    );
    if (!player) return;
    persistPlayerSeat(room, player.name);
    myPlayerIdRef.current = player.id;
    setMyPlayerId(player.id);
  };

  const forgetSeat = () => {
    clearPlayerSeat();
    myPlayerIdRef.current = null;
    setMyPlayerId(null);
  };

  const adoptRoom = (room: Room, playerName: string) => {
    rememberSeat(room, playerName);
    setCurrentRoom(room);
    setError(null);
  };

  // Health check function
  const checkServerHealth = async (serverUrl: string) => {
    try {
      // Convert WebSocket URL to HTTP URL for health check
      let httpUrl = serverUrl;
      if (httpUrl.startsWith('ws://')) {
        httpUrl = httpUrl.replace('ws://', 'http://');
      } else if (httpUrl.startsWith('wss://')) {
        httpUrl = httpUrl.replace('wss://', 'https://');
      }
      // If it doesn't start with http:// or https://, assume http://
      if (!httpUrl.startsWith('http://') && !httpUrl.startsWith('https://')) {
        httpUrl = `http://${httpUrl}`;
      }
      
      const healthUrl = `${httpUrl}/health`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setIsMaintenance(false);
        return true;
      } else {
        setIsMaintenance(true);
        return false;
      }
    } catch (error) {
      // Server is down or unreachable
      setIsMaintenance(true);
      return false;
    }
  };

  useEffect(() => {
    // Auto-detect server URL based on current hostname
    // If VITE_SERVER_URL is set, use it; otherwise use same hostname as frontend with port 3000
    const getServerUrl = () => {
      if (import.meta.env.VITE_SERVER_URL) {
        return import.meta.env.VITE_SERVER_URL;
      }
      // Use same hostname and protocol as current page, but port 3000
      const hostname = window.location.hostname;
      const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
      return `${protocol}//${hostname}:3000`;
    };
    
    const serverUrl = getServerUrl();
    
    // Initial health check
    checkServerHealth(serverUrl);
    
    // Set up periodic health check (every 10 seconds)
    const healthCheckInterval = setInterval(() => {
      checkServerHealth(serverUrl);
    }, 10000);
    const newSocket = io(serverUrl, {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      setIsMaintenance(false);
      setError(null);

      const inviteCode =
        typeof window !== 'undefined' ? parseInviteRoomCodeFromLocation() : null;
      let storedRoomCode = storage.getItem(STORAGE_KEYS.ROOM_CODE);
      const storedPlayerName = storage.getItem(STORAGE_KEYS.PLAYER_NAME);
      const storedPlayerId = storage.getItem(STORAGE_KEYS.PLAYER_ID);

      if (inviteCode && storedRoomCode && storedRoomCode.toUpperCase() !== inviteCode) {
        forgetSeat();
        storedRoomCode = null;
      }

      const canRejoin =
        !!storedRoomCode &&
        !!storedPlayerName &&
        !!newSocket.id &&
        newSocket.connected &&
        (!inviteCode || storedRoomCode.toUpperCase() === inviteCode);

      if (!canRejoin) return;

      const onRejoin = (room: Room | null) => {
        if (room) {
          setCurrentRoom(room);
          setError(null);
          rememberSeat(room, storedPlayerName);
          replaceInviteUrlWithHomeIfMatchingRoom(room.code);
        } else {
          forgetSeat();
          setError('Failed to rejoin room. Please try joining again.');
        }
      };

      if (storedPlayerId) {
        newSocket.emit('client:rejoin-room', storedRoomCode, storedPlayerName, storedPlayerId, onRejoin);
      } else {
        newSocket.emit('client:rejoin-room', storedRoomCode, storedPlayerName, onRejoin);
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      // Check if server is in maintenance when disconnected
      checkServerHealth(serverUrl);
    });
    
    newSocket.on('connect_error', () => {
      setIsConnected(false);
      checkServerHealth(serverUrl);
    });

    newSocket.on('server:room-created', (room: Room) => {
      setCurrentRoom(room);
      setError(null);
      rememberSeat(room, storage.getItem(STORAGE_KEYS.PLAYER_NAME) || room.players[0]?.name);
    });


    newSocket.on('server:player-left', (playerId: string) => {
      if (myPlayerIdRef.current === playerId) {
        setCurrentRoom(null);
        setGameState(null);
        setMyRole(null);
        setMyAnswer(null);
        setError(null);
        setRoomMessages([]);
        forgetSeat();
      }
    });

    newSocket.on('server:room-closed', () => {
      setCurrentRoom(null);
      setGameState(null);
      setMyRole(null);
      setMyAnswer(null);
      setError(null);
      setRoomMessages([]);
      forgetSeat();
    });

    newSocket.on('server:game-started', (state: GameState) => {
      // Update both states in batch to ensure they're synchronized
      setCurrentRoom(state.room);
      setGameState(state);
      setError(null);
      rememberSeat(state.room, storage.getItem(STORAGE_KEYS.PLAYER_NAME));
    });

    newSocket.on('server:role-assigned', (role: PlayerRole, answer: string) => {
      setMyRole(role);
      setMyAnswer(answer);
    });

    newSocket.on('server:vote-received', (voterId: string, votedPlayerId: string) => {
      setGameState(prev => {
        if (!prev || prev.room.phase !== 'voting') {
          return prev; // Only update if in voting phase
        }
        return {
          ...prev,
          votes: {
            ...prev.votes,
            [voterId]: votedPlayerId,
          },
        };
      });
    });

    newSocket.on('server:room-message', (message: RoomMessage) => {
      setRoomMessages((prev) => [...prev, message]);
    });

    newSocket.on('server:room-messages-sync', (messages: RoomMessage[]) => {
      setRoomMessages(Array.isArray(messages) ? messages : []);
    });

    newSocket.on('server:roles-revealed', (revealedGameState: GameState) => {
      setCurrentRoom(revealedGameState.room);
      setGameState(revealedGameState);
    });

    newSocket.on('server:player-role-revealed', (_playerId: string, _role: PlayerRole, revealedGameState: GameState) => {
      setCurrentRoom(revealedGameState.room);
      setGameState(revealedGameState);
    });

    newSocket.on('server:wordsmith-assigned', (wordsmithId: string) => {
      setCurrentRoom(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          wordsmithId: wordsmithId || undefined,
          players: prev.players.map(p => {
            if (p.id === wordsmithId && wordsmithId) {
              return { ...p, role: 'wordsmith' as PlayerRole };
            } else if (prev.wordsmithId === p.id && !wordsmithId) {
              return { ...p, role: undefined };
            }
            return p;
          }),
        };
      });
    });

    newSocket.on('server:word-submitted', (submittedGameState: GameState) => {
      setCurrentRoom(submittedGameState.room);
      setGameState(submittedGameState);
    });

    newSocket.on('server:waiting-for-wordsmith', (waitingGameState: GameState) => {
      setCurrentRoom(waitingGameState.room);
      setGameState(waitingGameState);
    });

    newSocket.on('server:voting-started', (votingGameState: GameState) => {
      setCurrentRoom(votingGameState.room);
      setGameState(votingGameState);
    });

    newSocket.on('server:timer-updated', (timer: GameState['timer']) => {
      setGameState((prev) => {
        if (!prev || !timer) return prev;
        return { ...prev, timer };
      });
    });

    newSocket.on('server:similar-minds-answer-registered', (payload: { answerId: string }) => {
      setMySimilarMindsAnswerId(payload.answerId);
    });

    newSocket.on('server:ace-attorney-prompt-ack', (payload: { question: string; judgment: string }) => {
      setMyAceAttorneyJudgment(payload.judgment);
    });

    newSocket.on('server:ace-attorney-answer-ack', (payload: { answer: string }) => {
      setMyAceAttorneyAnswer(payload.answer);
    });

    newSocket.on('server:game-theory-wheel-force-ack', (payload: { force: number }) => {
      setMyGameTheoryWheelForce(payload.force);
    });

    newSocket.on('server:stand-apart-pick-ack', (payload: { cardIndex: number }) => {
      setMyStandApartCardIndex(payload.cardIndex);
    });

    newSocket.on('server:schrodinger-observe-ack', (payload: { remainingSeconds: number; holdId: number }) => {
      setMySchrodingerObservation(payload);
    });

    newSocket.on('server:game-state-updated', (state: GameState) => {
      const storedPlayerName = storage.getItem(STORAGE_KEYS.PLAYER_NAME);
      const storedRoomCode = storage.getItem(STORAGE_KEYS.ROOM_CODE);
      const storedPlayerId = myPlayerIdRef.current || storage.getItem(STORAGE_KEYS.PLAYER_ID);
      const player = findSeatPlayer(state.room, storedPlayerId, storedPlayerName);
      const isPlayerInRoom = Boolean(player);
      
      if (isPlayerInRoom) {
        setCurrentRoom(state.room);
        // Always use the state from server, especially for votes - don't merge
        setGameState(state);
        if (player) {
          rememberSeat(state.room, player.name);
          
          if (
            state.room.phase === 'waiting' ||
            !isNashSchrodinger(state.room) ||
            state.schrodingerStage !== 'live'
          ) {
            setMySchrodingerObservation(null);
          }

          // If room is reset to WAITING phase, clear role and answer
          if (state.room.phase === 'waiting') {
            setMyRole(null);
            setMyAnswer(null);
            setMySimilarMindsAnswerId(null);
            setMyGameTheoryWheelForce(null);
            setMyStandApartCardIndex(null);
            setMyAceAttorneyJudgment(null);
            setMyAceAttorneyAnswer(null);
          } else if (state.room.phase === 'role_assignment' && state.room.gameType === 'similar_minds') {
            setMySimilarMindsAnswerId(null);
          } else if (state.room.phase === 'role_assignment' && state.room.gameType === 'ace_attorney') {
            setMyAceAttorneyJudgment(null);
            setMyAceAttorneyAnswer(null);
          } else if (
            isNashWheel(state.room) &&
            state.gameTheoryWheelStage === 'force' &&
            !state.gameTheoryWheelSubmittedPlayerIds?.includes(myPlayerIdRef.current ?? '')
          ) {
            setMyGameTheoryWheelForce(null);
          } else if (
            isNashStandApart(state.room) &&
            state.standApartStage === 'select' &&
            !state.standApartSubmittedPlayerIds?.includes(myPlayerIdRef.current ?? '')
          ) {
            setMyStandApartCardIndex(null);
          } else if (player.role) {
            // Restore role from player data if game has started
            setMyRole(player.role);
            // Restore answer based on game type and role
            if (state.room.gameType === 'deadpan') {
              // For Deadpan game, restore answer for Fact-Speaker
              if (player.role === PlayerRole.FACT_SPEAKER && state.truthDefinition) {
                setMyAnswer(state.truthDefinition);
              }
            } else if (state.room.gameType === 'undercover') {
              if (state.room.undercoverConfig?.mode === 'ghost') {
                setMyAnswer(null);
              } else if (player.role === PlayerRole.CIVILIAN && state.civilianWord) {
                setMyAnswer(state.civilianWord);
              } else if (player.role === PlayerRole.UNDERCOVER && state.undercoverWord) {
                setMyAnswer(state.undercoverWord);
              } else if (player.role === PlayerRole.BLANK) {
                setMyAnswer(''); // Blank players have no word
              }
            }
          }
        }
      } else if (storedPlayerId && storedRoomCode === state.room.code) {
        setCurrentRoom(null);
        setGameState(null);
        setMyRole(null);
        setMyAnswer(null);
        setMySimilarMindsAnswerId(null);
        setMyGameTheoryWheelForce(null);
        setMyStandApartCardIndex(null);
        setMySchrodingerObservation(null);
        setMyAceAttorneyJudgment(null);
        setMyAceAttorneyAnswer(null);
        forgetSeat();
      }
    });

    newSocket.on('server:error', (errorMessage: string) => {
      console.error('Server error:', errorMessage);
      setError(errorMessage);
    });

    setSocket(newSocket);

    return () => {
      clearInterval(healthCheckInterval);
      newSocket.close();
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        isMaintenance,
        currentRoom,
        gameState,
        error,
        myRole,
        myAnswer,
        mySimilarMindsAnswerId,
        myGameTheoryWheelForce,
        myStandApartCardIndex,
        mySchrodingerObservation,
        myAceAttorneyJudgment,
        myAceAttorneyAnswer,
        myPlayerId,
        adoptRoom,
        roomMessages,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

